import { jwtDecode } from "jwt-decode";
import { FiMail, FiPhone, FiMapPin, FiCalendar, FiHash, FiShield, FiClock, FiUser } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import { useGetUserByIdQuery } from "../../store/services/userServices";
import { getRoleLabel } from "../../utils/roleUtils";
import { isActiveUser } from "../users/UserTable";

const formatDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB");
};

const getInitials = (u) =>
  `${u?.first_name?.[0] || ""}${u?.last_name?.[0] || ""}`.toUpperCase() || "?";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3">
    <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200">
      <Icon size={15} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900 break-words">{value || "—"}</div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
    <h2 className="px-5 pt-4 text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h2>
    <div className="px-5 pb-2 divide-y divide-gray-100">{children}</div>
  </section>
);

export default function ProfilePage() {
  let tokenUser = {};
  try {
    tokenUser = jwtDecode(localStorage.getItem("giz_superaccess")) || {};
  } catch {
    // the dashboard only renders with a valid token
  }

  const { data, isLoading } = useGetUserByIdQuery(tokenUser.id, { skip: !tokenUser.id });
  // Show what the token already knows while the full record loads
  const user = { ...tokenUser, ...(data?.data || {}) };
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || "—";
  const active = isActiveUser(user);

  return (
    <ListPageShell>
      <div className="w-full max-w-3xl mx-auto flex flex-col gap-6">
        {/* Identity card */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="h-24 sm:h-28 bg-gradient-to-r from-[var(--secondary-color)]/25 via-[var(--secondary-color)]/10 to-transparent" />
          <div className="px-5 sm:px-6 pb-5 -mt-10 sm:-mt-12 flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-2xl bg-[var(--secondary-color)] text-white flex items-center justify-center text-2xl sm:text-3xl font-bold ring-4 ring-white shadow-md">
              {getInitials(user)}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight break-words">{fullName}</h1>
              <p className="text-sm text-gray-500 break-all">{user.email || "—"}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--secondary-color)]/10 px-3 py-1 text-xs font-semibold text-[var(--secondary-color)] ring-1 ring-inset ring-[var(--secondary-color)]/25">
                <FiShield size={12} />
                {getRoleLabel(user.role)}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
                  active ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-gray-100 text-gray-600 ring-gray-200"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                {active ? "Aktiv" : "Joaktiv"}
              </span>
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity ${isLoading ? "opacity-60" : ""}`}>
          <Section title="Të dhënat personale">
            <InfoRow icon={FiUser} label="Emri dhe mbiemri" value={fullName} />
            <InfoRow icon={FiMail} label="Email" value={user.email} />
            <InfoRow icon={FiPhone} label="Telefoni" value={user.phone_number} />
            <InfoRow icon={FiMapPin} label="Komuna" value={user.municipality_name} />
          </Section>

          <Section title="Llogaria">
            <InfoRow icon={FiHash} label="ID e përdoruesit" value={user.id ? `#${user.id}` : null} />
            <InfoRow icon={FiShield} label="Roli" value={getRoleLabel(user.role)} />
            <InfoRow icon={FiCalendar} label="Anëtar që nga" value={formatDate(user.created_at)} />
            <InfoRow icon={FiClock} label="Përditësuar më" value={formatDate(user.updated_at)} />
          </Section>
        </div>

        <p className="text-center text-xs text-gray-400">
          Për ndryshime në të dhënat e llogarisë, kontaktoni një administrator.
        </p>
      </div>
    </ListPageShell>
  );
}
